
import React from 'react';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useUserTeams } from '@/hooks/useUserRelationships';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { TeamsGrid } from '@/components/teams/TeamsGrid';
import { CreateTeamModal } from '@/components/teams/CreateTeamModal';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Team } from '@/types/database';

const Teams = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const { userTeams, loading } = useUserTeams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Check if user is authenticated
  const isAuthenticated = !!(supabaseUser || internalUser);

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Determine user role
  const userRole = profile?.role || internalProfile?.role || internalUser?.role;
  
  // Only coaches and team-owners can create teams
  const canCreateTeam = userRole === 'coach' || userRole === 'team-owner';

  // Get role-specific messaging
  const getRoleSpecificContent = () => {
    switch (userRole) {
      case 'coach':
      case 'team-owner':
        return {
          title: 'My Teams',
          description: 'Manage and view your teams',
          emptyStateMessage: "You haven't created any teams yet. Create your first team or ask to join an existing one.",
          buttonText: 'Create Your First Team'
        };
      case 'player':
        return {
          title: 'My Teams',
          description: 'View teams you are a member of',
          emptyStateMessage: "You haven't joined any teams yet. Ask a coach or team owner to invite you to a team.",
          buttonText: null
        };
      default:
        return {
          title: 'My Teams',
          description: 'View your teams',
          emptyStateMessage: "You haven't joined any teams yet. Ask a coach or team owner to invite you to a team.",
          buttonText: null
        };
    }
  };

  const roleContent = getRoleSpecificContent();

  // Handle team selection to navigate to team profile
  const handleTeamSelect = (team: Team) => {
    if (team.slug) {
      navigate(`/team/${team.slug}`);
    } else {
      console.error('Team does not have a slug:', team);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEOHead title="Teams" description="Browse and manage your sports teams on Players Lockeroom." path="/teams" />
      {/* Desktop Header */}
      {!isMobile && <Header />}
      {/* Mobile Header */}
      {isMobile && <MobileHeader />}
      
      <main className={`${
        isMobile 
          ? 'pt-16 mb-16 py-4 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                <Users className="h-8 w-8 text-sports-green" />
                {roleContent.title}
              </h1>
              <p className="text-gray-400 mt-2">
                {roleContent.description}
              </p>
            </div>
            
            {canCreateTeam && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-sports-green hover:bg-sports-green/80 text-black font-semibold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Team
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              <span className="ml-3 text-gray-400">Loading your teams...</span>
            </div>
          ) : userTeams.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Teams Found</h3>
              <p className="text-gray-500 mb-6">
                {roleContent.emptyStateMessage}
              </p>
              {canCreateTeam && roleContent.buttonText && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-sports-green hover:bg-sports-green/80 text-black font-semibold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {roleContent.buttonText}
                </Button>
              )}
            </div>
          ) : (
            <TeamsGrid teams={userTeams} loading={loading} onTeamSelect={handleTeamSelect} />
          )}
        </div>
      </main>
      
      {/* Desktop Footer */}
      {!isMobile && <Footer />}
      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}

      {/* Create Team Modal - Only show if user can create teams */}
      {canCreateTeam && showCreateModal && (
        <CreateTeamModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};

export default Teams;
